import React, { useMemo } from 'react'
import {
  ENTITY_TYPES,
  getFormattedAddress,
  updatePhoneValueSearch,
} from '@sales-center/common'
import { SmallLocationCardProps } from '../SmallLocationCard'
import { LargeLocationCardProps } from '../../AddServiceLocationForm/LargeLocationCard'
import { PhoneLink } from '@sales-center/components'
import AddressMapLink from '@user/features/MapView/AddressMapLink/AddressMapLink'
import PhoneIcon from '@mui/icons-material/Call'
import EmailIcon from '@mui/icons-material/MailOutline'
import { useTheme } from '@mui/material/styles'
import { getLocationCardStyles } from './useLocationCardStyles'
import Box from '@mui/material/Box/Box'

export function useData(
  props: (SmallLocationCardProps | LargeLocationCardProps) & {
    entityId?: number
    entityType?: ENTITY_TYPES
  },
) {
  const { location, entityId, entityType } = props

  const theme = useTheme()

  const styles = getLocationCardStyles(theme)

  return useMemo<React.ReactNode[]>(() => {
    const {
      firstName,
      lastName,
      company,
      street1,
      street2,
      city,
      stateAbbreviation,
      postalCode,
      emailAddresses,
      phoneNumber,
      phoneExt,
      altPhoneNumber,
      altPhoneExt,
      mobilePhone,
      externalLinkId,
      locationCode,
    } = location

    const fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim()
    const companyName = (company ?? '').trim()

    const address = getFormattedAddress(
      street1 ?? '',
      street2 ?? '',
      city ?? '',
      stateAbbreviation ?? '',
      postalCode ?? '',
    ).trim()

    const addressLink =
      address !== '' && entityId !== undefined ? (
        <AddressMapLink
          address={address}
          entityId={entityId}
          entityType={entityType}
          serviceLocationId={location.billingLocation ? undefined : location.id}
          latitude={location?.latitude}
          longitude={location?.longitude}
          textVariant={'body1'}
        >
          {address}
        </AddressMapLink>
      ) : address !== '' ? (
        <Box>{address}</Box>
      ) : null

    const formPhone = updatePhoneValueSearch(phoneNumber ?? '')

    const formAltPhone = updatePhoneValueSearch(altPhoneNumber ?? '')
    const formMobilePhone = updatePhoneValueSearch(mobilePhone ?? '')

    let firstControlRendered = !!address

    const formattedPhone = formPhone ? (
      <PhoneLink
        phone={formPhone}
        phoneExt={phoneExt || ''}
        includeExtInLink
        sx={firstControlRendered ? styles.links : styles.linksNoMargin}
        icon={<PhoneIcon sx={styles.icons} />}
      />
    ) : (
      ''
    )

    firstControlRendered = !!formattedPhone || firstControlRendered

    const formattedAltPhone = formAltPhone ? (
      <PhoneLink
        phone={formAltPhone}
        phoneExt={altPhoneExt || ''}
        includeExtInLink
        dataTestId={`location-card-phone`}
        sx={firstControlRendered ? styles.links : styles.linksNoMargin}
        icon={<PhoneIcon sx={styles.icons} />}
      />
    ) : Boolean(externalLinkId) &&
      Boolean(locationCode) &&
      Boolean(formMobilePhone) ? (
      <PhoneLink
        phone={formMobilePhone}
        phoneExt=""
        includeExtInLink
        dataTestId={`location-card-phone`}
        sx={firstControlRendered ? styles.links : styles.linksNoMargin}
        icon={<PhoneIcon sx={styles.icons} />}
      />
    ) : (
      ''
    )

    firstControlRendered = !!formattedAltPhone || firstControlRendered

    const emailCsv = [...emailAddresses]
      .filter((e) => e.emailAddress)
      .sort((a) => (a.isPrimary ? -1 : 1))
      .map((e, idx) => (
        <Box
          key={e.emailAddress}
          sx={
            firstControlRendered
              ? styles.links
              : idx === 0
                ? styles.linksNoMargin
                : styles.links
          }
        >
          <EmailIcon
            color="primary"
            sx={{
              color: '#1565C0',
              ...styles.icons,
            }}
          />
          <a
            href={`mailto:${e.emailAddress}`}
            key={e.emailAddress}
            target="_blank"
            rel="noreferrer"
            data-test-id={`location-card-email`}
          >
            {e.emailAddress}
          </a>
        </Box>
      ))

    return [
      fullName,
      companyName,
      location.billingLocation ? address : addressLink,
      formattedPhone,
      formattedAltPhone,
      ...emailCsv,
    ].filter(
      (x) =>
        React.isValidElement(x) || (typeof x === 'string' && x.trim() !== ''),
    )
  }, [location, entityId, entityType])
}
